import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminCertificates() {
  const [certs,   setCerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [title,   setTitle]   = useState('');
  const [order,   setOrder]   = useState(0);
  const [imgFile, setImgFile] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/certificates/admin')
      .then(r => setCerts(r.data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setTitle('');
    setOrder(0);
    setImgFile(null);
    setModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setTitle(c.title);
    setOrder(c.sortOrder);
    setImgFile(null);
    setModal(true);
  };

  const save = async () => {
    if (!title.trim())         { toast.error('Title is required.'); return; }
    if (!editing && !imgFile)  { toast.error('Please select an image.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title',     title);
      fd.append('sortOrder', order);
      if (imgFile) fd.append('image', imgFile);

      if (editing) {
        await api.put(`/certificates/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Certificate updated.');
      } else {
        await api.post('/certificates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Certificate added.');
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const toggleVisibility = async (id) => {
    try {
      const r = await api.put(`/certificates/${id}/toggle-visibility`);
      setCerts(prev => prev.map(c => c._id === id ? r.data.data : c));
      toast.success(r.data.data.isVisible ? 'Certificate is now visible.' : 'Certificate is now hidden.');
    } catch { toast.error('Failed to update visibility.'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this certificate?')) return;
    try {
      await api.delete(`/certificates/${id}`);
      setCerts(prev => prev.filter(c => c._id !== id));
      toast.success('Deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Certificates</h1>
          <p className="font-body text-sm text-secondary mt-1">Manage dealership certificates and achievements shown on the About page.</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <span className="material-symbols-outlined">add</span> Add Certificate
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-48 skeleton" />)}
        </div>
      ) : certs.length === 0 ? (
        <div className="text-center py-20">
          <span className="material-symbols-outlined text-[64px] text-outline-variant">workspace_premium</span>
          <p className="font-headline font-bold text-xl uppercase mt-3 text-secondary">No certificates yet</p>
          <p className="font-body text-sm text-secondary mt-1">Click "Add Certificate" to upload your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {certs.map(cert => (
            <div key={cert._id} className={`bg-white border ${cert.isVisible ? 'border-outline-variant/20' : 'border-dashed border-outline-variant/40 opacity-60'} flex flex-col`}>
              {/* Image */}
              <div className="relative aspect-square overflow-hidden bg-surface-container">
                <img src={cert.image} alt={cert.title} className="w-full h-full object-contain p-3" />
                {!cert.isVisible && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-[32px]">visibility_off</span>
                  </div>
                )}
              </div>

              {/* Info + Actions */}
              <div className="p-3 flex flex-col gap-2">
                <p className="font-body text-sm font-semibold text-on-surface truncate">{cert.title}</p>
                <div className="flex gap-1 justify-between items-center">
                  <span className={`font-label text-[9px] uppercase tracking-widest px-2 py-0.5 ${cert.isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {cert.isVisible ? 'Visible' : 'Hidden'}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => toggleVisibility(cert._id)}
                      title={cert.isVisible ? 'Hide' : 'Show'}
                      className={`p-1 transition-colors ${cert.isVisible ? 'text-green-600 hover:text-secondary' : 'text-secondary hover:text-green-600'}`}>
                      <span className="material-symbols-outlined text-[18px]">
                        {cert.isVisible ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                    <button onClick={() => openEdit(cert)} title="Edit"
                      className="p-1 text-secondary hover:text-primary-container transition-colors">
                      <span className="material-symbols-outlined text-[18px]">edit</span>
                    </button>
                    <button onClick={() => del(cert._id)} title="Delete"
                      className="p-1 text-secondary hover:text-error transition-colors">
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-outline-variant/20">
              <h2 className="font-headline font-black text-xl uppercase">
                {editing ? 'Edit Certificate' : 'Add Certificate'}
              </h2>
              <button onClick={() => setModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="input-label">Title *</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  className="input-field" placeholder="e.g. ISO Dealer Certificate 2024" />
              </div>

              <div>
                <label className="input-label">Sort Order</label>
                <input type="number" value={order} onChange={e => setOrder(e.target.value)}
                  className="input-field" placeholder="0" />
                <p className="font-label text-xs text-secondary mt-1">Lower number = appears first</p>
              </div>

              <div>
                <label className="input-label">{editing ? 'Replace Image (optional)' : 'Certificate Image *'}</label>
                <input ref={fileRef} type="file" accept="image/*"
                  onChange={e => setImgFile(e.target.files[0])}
                  className="input-field" />
                {editing?.image && !imgFile && (
                  <img src={editing.image} alt="" className="mt-2 h-24 object-contain border border-outline-variant/20" />
                )}
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-outline-variant/20">
              <button onClick={() => setModal(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary flex-1 justify-center disabled:opacity-70">
                {saving
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                  : <><span className="material-symbols-outlined">save</span> Save</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}